import {
	is_gen_2,
	get_gen_2_component_channel_structure,
	get_gen_2_control_component_channel_structure,
	lightDevicesWithoutModeSwitch,
	isXMODDevice,
} from './interface_helpers';
import { devices_config } from '@devices/index';
import type { Device } from 'src/types/Device';

/**
 * Checks if night mode is currently active based on active_between time range
 * @param active_between - Array of two time strings like ['22:00', '06:00']
 * @returns boolean - true if current time is within the active_between range
 */
const isNightModeActive = (active_between: string[]): boolean => {
	if (!active_between || !Array.isArray(active_between) || active_between.length !== 2) {
		return false;
	}

	const now = new Date();
	const currentMinutes = now.getHours() * 60 + now.getMinutes();

	const [startTime, endTime] = active_between;
	const [startHours, startMins] = startTime.split(':').map(Number);
	const [endHours, endMins] = endTime.split(':').map(Number);

	const startMinutes = startHours * 60 + startMins;
	const endMinutes = endHours * 60 + endMins;

	// Handle case where night mode spans midnight (e.g., 22:00 to 06:00)
	if (endMinutes <= startMinutes) {
		// Overnight range: current time is active if it's >= start OR <= end
		return currentMinutes >= startMinutes || currentMinutes <= endMinutes;
	} else {
		// Same-day range: current time is active if it's between start and end
		return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
	}
};

/**
 * Gets night mode settings for a device (Gen1 or Gen2)
 * @param device - The device object
 * @param settings - The device settings object
 * @returns Night mode settings object or null if not found
 */
const getNightModeSettings = (device: Device, settings: any) => {
	if (!settings) {
		return null;
	}

	if (is_gen_2(device)) {
		const componentKey = get_gen_2_component_channel_structure(device);
		return settings[componentKey]?.night_mode || null;
	}

	if (settings.night_mode) {
		return settings.night_mode;
	}

	if (
		device.category !== 'emeter' &&
		settings[device?.category?.split('s_')[0] + 's']?.[device.channel]?.night_mode
	) {
		return settings[device.category.split('s_')[0] + 's'][device.channel].night_mode;
	}

	return null;
};

/**
 * Checks if night mode is enabled for a device (regardless of whether it's currently active)
 * @param device - The device object
 * @param settings - The device settings object
 * @returns boolean - true if night mode is enabled
 */
export const isNightModeEnabled = (device: Device, settings: any): boolean => {
	const nightModeSettings = getNightModeSettings(device, settings);

	if (!nightModeSettings) {
		return false;
	}

	if (is_gen_2(device)) {
		return nightModeSettings.enable === true;
	}

	return nightModeSettings.enabled === true || nightModeSettings.enabled === 1;
};

/**
 * Checks if a device is currently turned off
 * @param device - The device object
 * @param status - The device status object
 * @returns boolean - true if device is off, false if on or status unavailable
 */
export const isDeviceOff = (device: Device, status: any): boolean => {
	if (!status?.online) {
		return false;
	}

	const isGen2 = is_gen_2(device);
	const categoryPrefix = device.category.split('s_')[0] + 's';

	if (typeof status[categoryPrefix] !== 'undefined') {
		if (device.category === 'emeter') {
			return !status.relays[0]?.ison;
		}
		return !status[categoryPrefix][device.channel]?.ison;
	}

	if (isGen2) {
		const component = get_gen_2_control_component_channel_structure(device);
		return !status?.[component]?.output;
	}

	return false;
};

/**
 * Checks if a device supports mode change (switching between color and white modes)
 * This is used mostly for night mode and inside the DeviceDrawer to check if the device supports color mode change and color temperature mode change at the same time.
 * @param deviceType - The device type string
 * @returns boolean - true if device supports mode change, false otherwise
 */
export const supportsColorModeChange = (deviceType: string): boolean => {
	const deviceConfig = devices_config[deviceType];

	if (
		!deviceType ||
		lightDevicesWithoutModeSwitch(deviceType) ||
		isXMODDevice(deviceType) ||
		!deviceConfig?.modes
	) {
		return false;
	}

	let hasColorOption = false;
	let hasColorTempOption = false;

	for (const modeKey in deviceConfig.modes) {
		const mode = deviceConfig.modes[modeKey];

		if (mode.color === true || mode.color) {
			hasColorOption = true;
		}

		/**
		 * Some configs declare CCT support as `color_temp: true` (e.g. SHRGBWW-01),
		 * others as an object with a `{ minValue, maxValue }` range. Both shapes
		 * are considered valid signals that the device exposes a white/CCT mode in
		 * the UI, matching how `ControlMode.svelte` derives `hasColorTempControl`.
		 */
		if (
			mode.color_temp === true ||
			(mode.color_temp &&
				typeof mode.color_temp === 'object' &&
				mode.color_temp.minValue &&
				mode.color_temp.maxValue)
		) {
			hasColorTempOption = true;
		}
	}

	return hasColorOption && hasColorTempOption;
};
